import {
  Box,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Button,
  Link
} from '@mui/material'
import {
  CheckCircle,
  ArrowBack
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

export default function TeamsIntegrationGuide() {
  const navigate = useNavigate()

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/admin/settings')}
        sx={{ mb: 3 }}
      >
        Back to Settings
      </Button>

      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
        Microsoft Teams Integration Setup Guide
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Connect your helpdesk to Microsoft Teams and receive real-time ticket notifications directly in your channel.
      </Typography>

      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Tenant-Wide Notifications
        </Typography>
        <Typography variant="body2">
          This integration is configured at the tenant level and will send notifications for <strong>ALL tickets</strong> across
          all your customer organisations and child sites to a single Teams channel. This ensures your support team never
          misses a ticket, regardless of which customer or site it comes from.
        </Typography>
      </Alert>

      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          What you'll need:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckCircle fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText primary="A Microsoft 365 account with access to Microsoft Teams" />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckCircle fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText primary="Access to Power Automate (included with Microsoft 365)" />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckCircle fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText primary="Admin access to your Helpdesk settings" />
          </ListItem>
        </List>
      </Alert>

      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
        Setup Instructions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Follow these step-by-step instructions to create your Power Automate flow:
      </Typography>

      <Stepper orientation="vertical">
        <Step active expanded>
          <StepLabel>
            <Typography variant="h6">Open Power Automate</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Go to{' '}
              <Link href="https://make.powerautomate.com" target="_blank" rel="noopener noreferrer">
                make.powerautomate.com
              </Link>{' '}
              and sign in with your work or school account.
            </Typography>
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                💡 Tip: Use your Microsoft 365 work or school account. If you don't have access, check with your IT administrator about Power Automate licensing.
              </Typography>
            </Box>
          </StepContent>
        </Step>

        <Step active expanded>
          <StepLabel>
            <Typography variant="h6">Create a New Flow</Typography>
          </StepLabel>
          <StepContent>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="1. Click 'Create' in the left sidebar"
                  secondary="Or click '+ New flow' if you see that option"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="2. Select 'Automated cloud flow'"
                  secondary="This will trigger automatically when the helpdesk sends notifications"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="3. Click 'Skip' when the modal appears"
                  secondary="The list of triggers doesn't include the one we need, so we'll add it manually"
                />
              </ListItem>
            </List>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                You'll now see the flow dashboard with options to add triggers and actions.
              </Typography>
            </Alert>
          </StepContent>
        </Step>

        <Step active expanded>
          <StepLabel>
            <Typography variant="h6">Add the Webhook Trigger</Typography>
          </StepLabel>
          <StepContent>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="1. Click 'Add a trigger' in the middle of the screen"
                  secondary="This opens the trigger selection menu on the left"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary='2. Search for "When a Teams webhook request is received"'
                  secondary="Type this in the search bar of the left-hand menu"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="3. Click to add the trigger"
                  secondary="It will be added to your flow canvas"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary='4. In the Parameters tab, under "Who can trigger the flow?" select "Anyone"'
                  secondary="This allows the helpdesk system to send notifications to this flow"
                />
              </ListItem>
            </List>
          </StepContent>
        </Step>

        <Step active expanded>
          <StepLabel>
            <Typography variant="h6">Name Your Flow</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Above the menu, next to the 'Back' button, you'll see "Untitled". Click here and give your flow a descriptive name.
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                Example: <strong>"Helpdesk Ticket Notifications"</strong>
              </Typography>
            </Box>
          </StepContent>
        </Step>

        <Step active expanded>
          <StepLabel>
            <Typography variant="h6">Add Teams Message Action</Typography>
          </StepLabel>
          <StepContent>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="1. Click the plus button (+) under your trigger"
                  secondary="This is on the main flow dashboard"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary='2. Search for "Post message in a chat or channel"'
                  secondary="Select this action from the results"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary='3. In the Parameters tab, keep "Post as" = "Flow Bot"'
                  secondary="This is the default and will send messages as the Power Automate bot"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary='4. Set "Post in" = "Channel"'
                  secondary="Select this option to post to a Teams channel"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="5. Connect to your Microsoft 365 account if needed"
                  secondary="Sign in if this is your first time using Teams actions"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="6. Select your Team and Channel"
                  secondary="Choose where you want the helpdesk notifications to appear"
                />
              </ListItem>
            </List>
          </StepContent>
        </Step>

        <Step active expanded>
          <StepLabel>
            <Typography variant="h6">Configure the Message Template</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              In the "Message" box, copy and paste the following template:
            </Typography>
            <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'common.white', mb: 2 }}>
              <pre style={{ margin: 0, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                <code>{`🎫 @{triggerBody()?['eventType']} - @{triggerBody()?['ticket']?['ticketNumber']}

Subject: @{triggerBody()?['ticket']?['subject']}
Priority: @{triggerBody()?['ticket']?['priority']}
Status: @{triggerBody()?['ticket']?['status']}
Assigned To: @{triggerBody()?['ticket']?['assignedTo']}

View ticket: @{triggerBody()?['ticket']?['url']}`}</code>
              </pre>
            </Paper>
            <Alert severity="success">
              <Typography variant="body2">
                💡 The dynamic content fields (e.g., @{'{'}triggerBody()?['ticket']?['subject']{'}'}@) will be automatically
                populated with actual ticket data when notifications are sent.
              </Typography>
            </Alert>
          </StepContent>
        </Step>

        <Step active expanded>
          <StepLabel>
            <Typography variant="h6">Save and Get Webhook URL</Typography>
          </StepLabel>
          <StepContent>
            <List dense>
              <ListItem>
                <ListItemText
                  primary='1. Click "Save" in the top right corner of the screen'
                  secondary="Wait for the flow to save successfully"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary='2. Click on your trigger "When a Teams webhook request is received"'
                  secondary="After saving, the webhook URL will appear in the trigger details"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="3. Copy the webhook URL"
                  secondary="This is your webhook URL - it will look like: https://prod-xx.westus.logic.azure.com:443/workflows/..."
                />
              </ListItem>
            </List>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Important: Keep this URL private!
              </Typography>
              <Typography variant="body2">
                Anyone with this URL can send messages to your Teams channel.
              </Typography>
            </Alert>
          </StepContent>
        </Step>

        <Step active expanded>
          <StepLabel>
            <Typography variant="h6">Configure Helpdesk Integration</Typography>
          </StepLabel>
          <StepContent>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="1. Go to Admin Settings → Integrations in your helpdesk"
                  secondary="You need Admin access to configure integrations"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="2. Paste your webhook URL"
                  secondary="Into the 'Power Automate Webhook URL' field"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="3. Enable Teams notifications"
                  secondary="Toggle the switch to 'Enabled'"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="4. Click 'Test Connection'"
                  secondary="This will send a test message to verify everything works"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="5. Save your settings"
                  secondary="Click 'Save Settings' to activate the integration"
                />
              </ListItem>
            </List>
          </StepContent>
        </Step>
      </Stepper>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
        Event Types
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your Teams channel will receive notifications for these events:
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="ticket_created"
                secondary="When a new support ticket is submitted by a customer"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="ticket_assigned"
                secondary="When a ticket is assigned to an agent"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="ticket_resolved"
                secondary="When a ticket is marked as resolved"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="ticket_updated"
                secondary="When a ticket status changes (e.g., from Open to In Progress)"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
        Troubleshooting
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Test connection failed
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="• Double-check the webhook URL - make sure you copied the entire URL" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Verify the Power Automate flow is turned ON (check the toggle in Power Automate)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Check if your firewall is blocking outbound HTTPS requests" />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Not receiving notifications
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="• Verify that Teams notifications are enabled in Settings" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Check the 'Run history' in Power Automate to see if requests are being received" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Make sure the Power Automate flow hasn't been paused or turned off" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Verify the Teams channel still exists and the bot has permission to post" />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Messages not formatted correctly
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="• Check that you pasted the correct JSON schema in the HTTP trigger" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Verify you're using dynamic content correctly (e.g., triggerBody()?['ticket']?['subject'])" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Test the flow manually in Power Automate with sample data" />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Alert severity="info">
        <Typography variant="body2">
          <strong>Need help?</strong> Contact your administrator or reach out to support if you're having trouble setting up the integration.
        </Typography>
      </Alert>
    </Box>
  )
}
